import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Updated color palette matching the new design system
const colors = {
  primary: [241, 103, 69],        // #F16745 - Primary orange
  white: [255, 255, 255],         // White text on primary
  foreground: [51, 46, 46],       // #332E2E - Main text color
  mutedForeground: [51, 46, 46], // #332E2E - Muted text
  background: [241, 235, 219],    // #F1EBDB - Main background
  secondary: [243, 240, 232],     // #F3F0E8 - Secondary background
  secondaryBorder: [227, 223, 218], // #E3DFDA - Border color
  primary10: [241, 103, 69, 0.1], // Primary with 10% opacity
};

// Function to extract all CSS from the current document
const getAllStyles = () => {
  const allCSS = [];
  
  // Get inline styles from style tags
  const styleTags = document.querySelectorAll('style');
  styleTags.forEach(styleTag => {
    if (styleTag.textContent) {
      allCSS.push(styleTag.textContent);
    }
  });
  
  // Get external stylesheets
  const linkTags = document.querySelectorAll('link[rel="stylesheet"]');
  linkTags.forEach(link => {
    if (link.href) {
      allCSS.push(`@import url('${link.href}');`);
    }
  });
  
  // Try to get stylesheet rules
  try {
    for (let i = 0; i < document.styleSheets.length; i++) {
      const styleSheet = document.styleSheets[i];
      try {
        const rules = styleSheet.cssRules || styleSheet.rules;
        if (rules) {
          for (let j = 0; j < rules.length; j++) {
            if (rules[j].cssText) {
              allCSS.push(rules[j].cssText);
            }
          }
        }
      } catch (e) {
        // Handle cross-origin stylesheet access silently
      }
    }
  } catch (error) {
    // Handle error silently
  }
  
  return allCSS.join('\n');
};

// Function to remove elements marked for PDF exclusion
const cleanCloneForPDF = (clonedElement) => {
  const elementsToRemove = clonedElement.querySelectorAll('.pdf-export-remove');
  elementsToRemove.forEach(element => {
    element.remove();
  });
  return clonedElement;
};

// Core cloning-based PDF export function
const exportPdfByCloning = async (sourceElementId, filename) => {
  // Find the live element
  const sourceElement = document.getElementById(sourceElementId);
  if (!sourceElement) {
    throw new Error(`Element with ID "${sourceElementId}" not found`);
  }
  
  // Create off-screen container
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-9999px';
  container.style.top = '-9999px';
  container.style.width = '1200px';
  container.style.height = 'auto';
  container.style.backgroundColor = '#F1EBDB';
  container.style.fontFamily = "'Figtree', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
  container.style.padding = '24px';
  container.style.overflow = 'visible';
  container.style.zIndex = '-9999';
  container.className = 'pdf-export-container';
  
  try {
    // Clone the source element
    const clonedElement = sourceElement.cloneNode(true);
    
    // Clean the clone by removing PDF-excluded elements
    cleanCloneForPDF(clonedElement);
    
    // Get all current styles
    const allStyles = getAllStyles();
    
    // Create a style element with all current CSS
    const styleElement = document.createElement('style');
    styleElement.textContent = allStyles + `
      /* PDF Export Specific Fixes for Icon Alignment - Only apply within PDF container */
      .pdf-export-container .pdf-export-fix svg {
        vertical-align: middle !important;
        transform: translateY(2px) !important;
      }
      
      /* Fix for mail and calendar icons in meta info */
      .pdf-export-container .flex.items-center svg[size="20"] {
        transform: translateY(5px) !important;
        vertical-align: middle !important;
      }
      
      /* Add padding to containers to prevent icon clipping */
      .pdf-export-container .flex.items-center {
        padding-top: 8px !important;
        padding-bottom: 8px !important;
        min-height: 32px !important;
        align-items: center !important;
        overflow: visible !important;
      }
      
      /* Add padding to meta info containers */
      .pdf-export-container .text-sm.text-muted-foreground {
        padding-top: 8px !important;
        padding-bottom: 8px !important;
        min-height: 28px !important;
        overflow: visible !important;
      }
      
      /* Add padding to the question cards */
      .pdf-export-container .p-4 {
        padding-top: 20px !important;
        padding-bottom: 20px !important;
        overflow: visible !important;
      }
      
      /* Ensure question content has enough height */
      .pdf-export-container .flex.items-center.gap-4 {
        min-height: 40px !important;
        padding-top: 5px !important;
        padding-bottom: 5px !important;
        overflow: visible !important;
      }
      
      /* Fix for user icon in header */
      .pdf-export-container .w-10.h-10.bg-primary.rounded-lg {
        margin-top: 15px !important;
        overflow: visible !important;
      }
      
      .pdf-export-container .w-10.h-10.bg-primary.rounded-lg svg {
        transform: translateY(2px) !important;
        vertical-align: middle !important;
      }
      
      /* Fix for question number circles */
      .pdf-export-container .w-8.h-8.bg-primary.rounded-lg {
        align-items: center !important;
        justify-content: center !important;
        display: flex !important;
        margin-top: 10px !important;
        overflow: visible !important;
      }
      
      .pdf-export-container .w-8.h-8.bg-primary.rounded-lg span {
        line-height: 1 !important;
        transform: translateY(-2px) !important;
      }
      
      /* Additional selectors for mail and calendar icons */
      .pdf-export-container svg {
        vertical-align: middle !important;
        overflow: visible !important;
      }
      
      /* Specific targeting for lucide icons */
      .pdf-export-container .flex.items-center .mr-1 {
        transform: translateY(2px) !important;
      }
      
      /* Target icons in meta info section */
      .pdf-export-container .text-sm.text-muted-foreground svg {
        transform: translateY(2px) !important;
      }
      
      /* Ensure all containers allow overflow */
      .pdf-export-container * {
        overflow: visible !important;
      }
    `;
    
    // Add styles and content to container
    container.appendChild(styleElement);
    container.appendChild(clonedElement);
    
    // Temporarily add container to document
    document.body.appendChild(container);
    
    // Force reflow to apply styles
    container.offsetHeight;
    
    // Give time for styles to apply
    await new Promise(resolve => setTimeout(resolve, 200));
    
    // Capture with html2canvas
    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#F1EBDB',
      width: 1200,
      height: container.scrollHeight,
      onclone: (clonedDoc) => {
        clonedDoc.documentElement.style.backgroundColor = '#F1EBDB';
        clonedDoc.body.style.backgroundColor = '#F1EBDB';
      }
    });
    
    if (canvas.width === 0 || canvas.height === 0) {
      throw new Error('Canvas is empty - no content was captured');
    }
    
    // Create PDF from canvas
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    
    // Set PDF background color
    pdf.setFillColor(colors.background[0], colors.background[1], colors.background[2]);
    pdf.rect(0, 0, pdf.internal.pageSize.getWidth(), pdf.internal.pageSize.getHeight(), 'F');
    
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pdfWidth - 20;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    
    let heightLeft = imgHeight;
    let position = 10;
    
    // Add image to first page
    pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
    heightLeft -= (pdfHeight - 20);
    
    // Handle multiple pages
    while (heightLeft > 0) {
      position = -(imgHeight - (pdfHeight - 20 - heightLeft));
      pdf.addPage();
      pdf.setFillColor(colors.background[0], colors.background[1], colors.background[2]);
      pdf.rect(0, 0, pdf.internal.pageSize.getWidth(), pdf.internal.pageSize.getHeight(), 'F');
      pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
      heightLeft -= (pdfHeight - 20);
    }
    
    // Save the PDF
    pdf.save(filename);
    
    return {
      success: true,
      message: `PDF generated successfully`,
      pages: pdf.internal.getNumberOfPages()
    };
    
  } catch (error) {
    throw new Error(`Failed to generate PDF: ${error.message}`);
  } finally {
    // Always clean up
    if (container.parentNode) {
      container.parentNode.removeChild(container);
    }
  }
};

// Main export function - updated to use cloning method
export const exportQuestionsToPDF = async (questions, filename = 'interview-questions.pdf', sourceElementId = 'pdf-export-container') => {
  if (!questions || questions.length === 0) {
    throw new Error('No questions to export');
  }
  
  try {
    const result = await exportPdfByCloning(sourceElementId, filename);
    
    return {
      success: true,
      message: `Exported ${questions.length} questions to ${filename}`,
      count: questions.length,
      pages: result.pages
    };
    
  } catch (error) {
    throw new Error(`Failed to generate PDF: ${error.message}`);
  }
};

// Filename generator
export const generatePDFFilename = (userId, userName) => {
  const timestamp = new Date().toISOString().split('T')[0];
  const userPart = userName ? userName.replace(/[^a-zA-Z0-9]/g, '-') : `user-${userId}`;
  return `interview-questions-${userPart}-${timestamp}.pdf`;
};